import { useState, useRef, useEffect } from 'react'
import './ReactionPicker.css'

interface ReactionPickerProps {
  onSelect: (emoji: string) => void
  onClose?: () => void
  position?: { top: number; left: number } | { bottom: number; right: number }
}

const COMMON_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '🎉', '💯', '✅', '❌']

export function ReactionPicker({ onSelect, onClose, position }: ReactionPickerProps) {
  const [isOpen, setIsOpen] = useState(true)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        onClose?.()
      }
    }

    // Use a small delay to prevent immediate closure when the button is clicked
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleSelect = (emoji: string) => {
    onSelect(emoji)
    setIsOpen(false)
    onClose?.()
  }

  if (!isOpen) return null

  const style: React.CSSProperties = position
    ? 'top' in position
      ? { top: `${position.top}px`, left: `${position.left}px` }
      : { bottom: `${position.bottom}px`, right: `${position.right}px` }
    : {}

  return (
    <div ref={pickerRef} className="reaction-picker" style={style}>
      <div className="reaction-picker-grid">
        {COMMON_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            className="reaction-picker-item"
            onClick={() => handleSelect(emoji)}
            aria-label={`React with ${emoji}`}
            title={emoji}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}
